"use client";

import * as React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Icon, type IconName } from "@/components/ui/icon";
import { logo } from "@/lib/logo-dev";
import { cn } from "@/lib/utils";
import {
  candidateLogoVariants,
  logoUrlSize,
  type CandidateLogoSize,
} from "./logo-variants";

const PLACEHOLDER_ICON_SIZE_PX: Record<CandidateLogoSize, number> = {
  sm: 10,
  md: 12,
  lg: 24,
};

export type CandidateLogoBaseProps = Omit<
  React.ComponentProps<typeof Avatar>,
  "size"
> & {
  domain?: string | null;
  name?: string | null;
  size?: CandidateLogoSize;
  /** When false, only resolves a logo from `domain` (no `/name/...` fallback). */
  nameFallback?: boolean;
  fallbackIcon: IconName;
  fallbackLabel: string;
};

export function CandidateLogoBase({
  domain,
  name,
  size = "md",
  nameFallback,
  fallbackIcon,
  fallbackLabel,
  className,
  ...props
}: CandidateLogoBaseProps) {
  const url = logo({
    domain,
    name,
    size: logoUrlSize(size),
    nameFallback,
  });
  return (
    <Avatar
      aria-label={fallbackLabel}
      className={cn(candidateLogoVariants({ size }), className)}
      {...props}
    >
      <AvatarImage src={url ?? undefined} alt="" />
      <AvatarFallback>
        <Icon
          name={fallbackIcon}
          size={PLACEHOLDER_ICON_SIZE_PX[size]}
          className="text-foreground/40"
        />
      </AvatarFallback>
    </Avatar>
  );
}
